import { EventData } from 'features/systemLens'
import {
  CHART_COLORS,
  DEFAULT_CHART_SETTINGS,
  UPLOT_TICK_FONT,
} from 'features/timeSeries/config/constants'
import {
  nearestBucketIndex,
  scoreCadenceMs,
  SENTINEL_OUT_OF_BAND_COLOR,
  SentinelBandOverlay,
  SIGMA_WARN,
} from 'features/timeSeries/hooks/useSentinelScores'
import uPlot from 'uplot'

import {
  getEventLineColor,
  getEventLineDash,
  getEventLineWidth,
  getThresholdColor,
  getThresholdLabel,
} from './chartHelpers'

interface ThresholdValue {
  value: number
  type: string
  sensors: string[]
}

interface ThresholdPluginOptions {
  thresholds: ThresholdValue[]
  sensorAxisMapping: Record<
    string,
    { axis: string; side: 'left' | 'right'; index: number }
  >
  enhancedSensorEntries: Array<{ id: string }>
  singleScaleMode: boolean
}

export function thresholdsPlugin(
  options: ThresholdPluginOptions
): uPlot.Plugin {
  const {
    thresholds,
    sensorAxisMapping,
    enhancedSensorEntries,
    singleScaleMode,
  } = options

  return {
    hooks: {
      draw: (u: uPlot) => {
        const ctx = u.ctx

        thresholds.forEach((threshold) => {
          const firstSensor = threshold.sensors[0]
          const sensor = enhancedSensorEntries.find((e) =>
            e.id.includes(firstSensor)
          )
          const sensorId = sensor?.id || firstSensor
          const scaleKey = singleScaleMode
            ? 'y'
            : sensorAxisMapping[sensorId]
              ? sensorId
              : 'y'

          const yPos = u.valToPos(threshold.value, scaleKey, true)

          // Skip if outside the plotting area
          if (yPos < u.bbox.top || yPos > u.bbox.top + u.bbox.height) return

          const color = getThresholdColor(threshold.type)
          const label = `${getThresholdLabel(threshold.type)} (${threshold.sensors.join(', ')})`

          ctx.save()
          ctx.beginPath()
          ctx.setLineDash([6, 4])
          ctx.lineWidth = 2
          ctx.strokeStyle = color
          ctx.moveTo(u.bbox.left, yPos)
          ctx.lineTo(u.bbox.left + u.bbox.width, yPos)
          ctx.stroke()

          ctx.font = `${DEFAULT_CHART_SETTINGS.TICK_FONT_SIZE * devicePixelRatio}px ${DEFAULT_CHART_SETTINGS.FONT_FAMILY}`
          ctx.fillStyle = color
          ctx.textAlign =
            sensorAxisMapping[sensorId]?.side === 'right' ? 'right' : 'left'
          ctx.textBaseline = threshold.type.includes('upper') ? 'bottom' : 'top'

          const xTextPos =
            sensorAxisMapping[sensorId]?.side === 'right'
              ? u.bbox.left + u.bbox.width * 0.9
              : u.bbox.left + u.bbox.width * 0.1
          ctx.fillText(label, xTextPos, yPos)
          ctx.restore()
        })
      },
    },
  }
}

interface EventMarkersPluginOptions {
  events: EventData[]
}

export function eventMarkersPlugin(
  options: EventMarkersPluginOptions
): uPlot.Plugin {
  const { events } = options

  return {
    hooks: {
      draw: (u: uPlot) => {
        if (events.length === 0) return

        const ctx = u.ctx

        events.forEach((event) => {
          const timestamp = new Date(event.timestamp).getTime()
          const xPos = u.valToPos(timestamp, 'x', true)

          // Skip if outside plotting area
          if (xPos < u.bbox.left || xPos > u.bbox.left + u.bbox.width) return

          const lineColor = getEventLineColor(event.metadata?.priority)
          const lineWidth = getEventLineWidth(event.metadata?.priority)
          const lineDash = getEventLineDash(event.metadata?.status)

          ctx.save()
          ctx.beginPath()
          ctx.setLineDash(lineDash)
          ctx.lineWidth = lineWidth * devicePixelRatio
          ctx.strokeStyle = lineColor
          ctx.moveTo(xPos, u.bbox.top)
          ctx.lineTo(xPos, u.bbox.top + u.bbox.height)
          ctx.stroke()

          // Draw annotation label
          const annotationText = event.type
          const fontSize =
            DEFAULT_CHART_SETTINGS.TICK_FONT_SIZE * devicePixelRatio
          ctx.font = `${fontSize}px ${DEFAULT_CHART_SETTINGS.FONT_FAMILY}`

          const textMetrics = ctx.measureText(annotationText)
          const padding = 2 * devicePixelRatio
          const textHeight = fontSize

          const labelX = xPos - textMetrics.width / 2 - padding
          const labelY = u.bbox.top - textHeight - padding * 3

          ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
          ctx.strokeStyle = lineColor
          ctx.lineWidth = devicePixelRatio
          ctx.setLineDash([])
          ctx.fillRect(
            labelX,
            labelY,
            textMetrics.width + padding * 2,
            textHeight + padding * 2
          )
          ctx.strokeRect(
            labelX,
            labelY,
            textMetrics.width + padding * 2,
            textHeight + padding * 2
          )

          ctx.fillStyle = lineColor
          ctx.textAlign = 'center'
          ctx.textBaseline = 'bottom'
          ctx.fillText(annotationText, xPos, u.bbox.top - padding)
          ctx.restore()
        })
      },
    },
  }
}

interface SentinelBandsPluginOptions {
  /** Reuses the hook's overlay type so the shapes cannot drift apart. */
  overlays: Array<
    Pick<
      SentinelBandOverlay,
      | 'targetSensorId'
      | 'label'
      | 'color'
      | 'xs'
      | 'upper'
      | 'lower'
      | 'center'
      | 'actual'
      | 'sigma'
    >
  >
  sensorAxisMapping: Record<
    string,
    { axis: string; side: 'left' | 'right'; index: number }
  >
  singleScaleMode: boolean
  /**
   * Styling for the center (expected) line. Sentinels keep the default: a
   * faint dotted white trace that reads as texture inside the band. The
   * experiment overlay passes a prominent style so the model's PREDICTION is
   * a first-class line the user can follow against the actual pen.
   */
  centerLine?: {
    color?: string
    alpha?: number
    widthPx?: number
    /** Dash pattern in CSS px, scaled by devicePixelRatio when drawn. */
    dashPx?: [number, number]
  }
}

/**
 * Sentinel alerting ranges: a mostly-transparent fill between the upper and
 * lower band (expected ± band) with a dotted center line tracing the model's
 * expected value, drawn around each sentinel's target pen on that pen's own
 * y-scale. Edge strokes exist but are currently fully transparent — the fill
 * marks the band's extent.
 */
export function sentinelBandsPlugin(
  options: SentinelBandsPluginOptions
): uPlot.Plugin {
  const { overlays, sensorAxisMapping, singleScaleMode } = options
  const centerLine = {
    color: '#ffffff',
    alpha: 0.2,
    widthPx: 1.5,
    dashPx: [3, 5] as [number, number],
    ...options.centerLine,
  }
  type SentinelOverlay = SentinelBandsPluginOptions['overlays'][number]

  /**
   * The y-scale the overlay's target pen draws on, or null when the pen has
   * no scale on this chart (multi-scale mode creates one fixed-range scale
   * per mapped sensor and no 'y' scale at all). Null means SKIP the overlay:
   * the previous silent fallback to 'y' fed valToPos a nonexistent scale and
   * rendered the band at meaningless positions.
   */
  const scaleKeyFor = (targetSensorId: string): string | null =>
    singleScaleMode
      ? 'y'
      : sensorAxisMapping[targetSensorId]
        ? targetSensorId
        : null

  /**
   * Score buckets are NOT uniformly spaced — the service's cleaning gates
   * drop rows, leaving multi-hour holes in `xs`. Everything time-shaped in
   * this plugin (bucket matching, run contiguity, block widths) goes through
   * this geometry instead of assuming `xs[1] - xs[0]` is the cadence.
   */
  const bucketGeometry = (overlay: SentinelOverlay) => {
    const { xs } = overlay
    const cadence = scoreCadenceMs(xs)
    return {
      cadence,
      /** A cleaning gap sits between bucket i-1 and i: runs must break. */
      gapBefore: (i: number) => i > 0 && xs[i] - xs[i - 1] > cadence * 1.5,
      /** Half-width toward the previous bucket, never across a gap. */
      halfLeft: (i: number) =>
        i > 0 ? Math.min(xs[i] - xs[i - 1], cadence) / 2 : cadence / 2,
      /** Half-width toward the next bucket, never across a gap. */
      halfRight: (i: number) =>
        i < xs.length - 1
          ? Math.min(xs[i + 1] - xs[i], cadence) / 2
          : cadence / 2,
      /** The bucket a chart sample belongs to, or null inside a gap. */
      bucketFor: (timestampMs: number): number | null => {
        const index = nearestBucketIndex(xs, timestampMs)
        if (index === null) return null
        return Math.abs(xs[index] - timestampMs) > cadence * 0.75 ? null : index
      },
    }
  }

  /**
   * The band interior as one polygon per contiguous non-null run (up along
   * the upper edge, back along the lower). Runs also break at cleaning gaps
   * so the band never interpolates across spans the model was gated out of.
   * Filled in `draw` as the shaded region and used in `drawClear` to clip
   * the glow out of the band.
   */
  const buildBandPath = (
    u: uPlot,
    overlay: SentinelOverlay,
    scaleKey: string,
    gapBefore: (i: number) => boolean
  ): Path2D => {
    const path = new Path2D()
    let runStart = -1
    const closeRun = (endExclusive: number) => {
      if (runStart < 0) return
      for (let i = runStart; i < endExclusive; i++) {
        const x = u.valToPos(overlay.xs[i], 'x', true)
        const y = u.valToPos(overlay.upper[i] as number, scaleKey, true)
        if (i === runStart) path.moveTo(x, y)
        else path.lineTo(x, y)
      }
      for (let i = endExclusive - 1; i >= runStart; i--) {
        path.lineTo(
          u.valToPos(overlay.xs[i], 'x', true),
          u.valToPos(overlay.lower[i] as number, scaleKey, true)
        )
      }
      path.closePath()
      runStart = -1
    }
    for (let i = 0; i < overlay.xs.length; i++) {
      const hasValue =
        overlay.upper[i] !== null &&
        overlay.upper[i] !== undefined &&
        overlay.lower[i] !== null &&
        overlay.lower[i] !== undefined
      if (hasValue) {
        if (runStart >= 0 && gapBefore(i)) closeRun(i)
        if (runStart < 0) runStart = i
      } else {
        closeRun(i)
      }
    }
    closeRun(overlay.xs.length)
    return path
  }

  return {
    hooks: {
      // Fires after the canvas clears but before the series paint, so
      // everything drawn here sits BEHIND the pen lines.
      drawClear: (u: uPlot) => {
        if (overlays.length === 0) return

        const ctx = u.ctx
        ctx.save()
        ctx.beginPath()
        ctx.rect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height)
        ctx.clip()

        overlays.forEach((overlay) => {
          if (overlay.xs.length === 0) return
          const scaleKey = scaleKeyFor(overlay.targetSensorId)
          if (scaleKey === null) return
          const outOfBandAt = (index: number) => {
            const sigma = overlay.sigma[index]
            return (
              sigma !== null &&
              sigma !== undefined &&
              Math.abs(sigma) >= SIGMA_WARN
            )
          }
          const { gapBefore, halfLeft, halfRight, bucketFor } =
            bucketGeometry(overlay)

          // Out-of-band glow, drawn behind the pen. A fixed-width stroke
          // along the scored mean vanishes under a noisy pen whose raw
          // spread is wider than the stroke, and a stroked path renders
          // nothing at all for a single-bucket run. So instead: build the
          // pen's raw min/max envelope per score bucket (falling back to
          // the scored actual where the pen has no samples) and fill a
          // padded blob per contiguous run — it always reaches past the
          // data's own fuzz and has area even for one bad bucket.
          const penSeriesIndex = u.series.findIndex(
            (series) =>
              (series as { _sensorId?: string })._sensorId ===
              overlay.targetSensorId
          )
          const bucketMin = new Array<number | null>(overlay.xs.length).fill(
            null
          )
          const bucketMax = new Array<number | null>(overlay.xs.length).fill(
            null
          )
          if (penSeriesIndex > 0) {
            const times = u.data[0]
            const values = u.data[penSeriesIndex]
            for (let j = 0; j < times.length; j++) {
              const value = values?.[j]
              if (value === null || value === undefined) continue
              // Nearest-actual-bucket matching: `xs` has cleaning gaps, so
              // uniform index arithmetic mis-assigns every sample after the
              // first gap. Samples inside a gap belong to no bucket at all.
              const bucket = bucketFor(times[j])
              if (bucket === null) continue
              if (!outOfBandAt(bucket)) continue
              const currentMin = bucketMin[bucket]
              const currentMax = bucketMax[bucket]
              if (currentMin === null || value < currentMin)
                bucketMin[bucket] = value
              if (currentMax === null || value > currentMax)
                bucketMax[bucket] = value
            }
          }

          // The 2σ band takes priority over the glow: clip to the plot area
          // MINUS the band polygon (even-odd), so the glow never overlaps
          // the model's shaded region.
          ctx.save()
          const glowClip = new Path2D()
          glowClip.rect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height)
          glowClip.addPath(buildBandPath(u, overlay, scaleKey, gapBefore))
          ctx.clip(glowClip, 'evenodd')

          ctx.setLineDash([])
          ctx.fillStyle = SENTINEL_OUT_OF_BAND_COLOR
          ctx.shadowColor = SENTINEL_OUT_OF_BAND_COLOR
          ctx.shadowBlur = 8 * devicePixelRatio
          ctx.globalAlpha = 0.85
          const pad = 4 * devicePixelRatio

          let runStart = -1
          const fillGlowRun = (endExclusive: number) => {
            if (runStart < 0) return
            ctx.beginPath()
            for (let i = runStart; i < endExclusive; i++) {
              const top = bucketMax[i] ?? overlay.actual[i]
              if (top === null || top === undefined) continue
              const x =
                i === runStart
                  ? u.valToPos(overlay.xs[i] - halfLeft(i), 'x', true)
                  : u.valToPos(overlay.xs[i], 'x', true)
              const y = u.valToPos(top, scaleKey, true) - pad
              if (i === runStart) ctx.moveTo(x, y)
              else ctx.lineTo(x, y)
            }
            for (let i = endExclusive - 1; i >= runStart; i--) {
              const bottom = bucketMin[i] ?? overlay.actual[i]
              if (bottom === null || bottom === undefined) continue
              const x =
                i === endExclusive - 1
                  ? u.valToPos(overlay.xs[i] + halfRight(i), 'x', true)
                  : u.valToPos(overlay.xs[i], 'x', true)
              ctx.lineTo(x, u.valToPos(bottom, scaleKey, true) + pad)
            }
            ctx.closePath()
            ctx.fill()
            runStart = -1
          }
          for (let i = 0; i < overlay.xs.length; i++) {
            const hasAnchor =
              (bucketMax[i] ?? overlay.actual[i]) !== null &&
              (bucketMax[i] ?? overlay.actual[i]) !== undefined
            if (outOfBandAt(i) && hasAnchor) {
              // A cleaning gap severs the run: two excursions separated by a
              // gated-out span are separate blobs, not one smear across it.
              if (runStart >= 0 && gapBefore(i)) fillGlowRun(i)
              if (runStart < 0) runStart = i
            } else {
              fillGlowRun(i)
            }
          }
          fillGlowRun(overlay.xs.length)

          ctx.restore()
        })

        ctx.restore()
      },
      draw: (u: uPlot) => {
        if (overlays.length === 0) return

        const ctx = u.ctx
        ctx.save()
        ctx.beginPath()
        ctx.rect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height)
        ctx.clip()

        overlays.forEach((overlay) => {
          if (overlay.xs.length === 0) return
          const scaleKey = scaleKeyFor(overlay.targetSensorId)
          if (scaleKey === null) return
          const { gapBefore, halfLeft, halfRight } = bucketGeometry(overlay)

          // Shaded band interior. Upper and lower both derive from the same
          // expected series, so their null gaps align.
          ctx.globalAlpha = 0.2
          ctx.fillStyle = overlay.color
          ctx.fill(buildBandPath(u, overlay, scaleKey, gapBefore))
          ctx.globalAlpha = 1
          const strokeSeries = (values: (number | null)[]) => {
            let penDown = false
            for (let i = 0; i < overlay.xs.length; i++) {
              const value = values[i]
              if (value === null || value === undefined) {
                penDown = false
                continue
              }
              // Lift the pen across cleaning gaps: the model has no
              // expectation inside a gated-out span, so don't draw one.
              if (gapBefore(i)) penDown = false
              const x = u.valToPos(overlay.xs[i], 'x', true)
              const y = u.valToPos(value, scaleKey, true)
              if (penDown) {
                ctx.lineTo(x, y)
              } else {
                ctx.moveTo(x, y)
                penDown = true
              }
            }
            ctx.stroke()
            ctx.globalAlpha = 1
          }

          ;[overlay.upper, overlay.lower].forEach((edge) => {
            ctx.beginPath()
            // Edges fully transparent for now: the shaded fill alone marks
            // the band's extent. Raise the alpha to bring them back.
            ctx.setLineDash([])
            ctx.lineWidth = 1.5 * devicePixelRatio
            ctx.strokeStyle = overlay.color
            ctx.globalAlpha = 0.0
            strokeSeries(edge)
          })

          // Center line tracing the expected value, so the model's
          // prediction can be followed through the band. Sentinels draw it
          // faint (texture inside the tinted fill); the experiment overlay
          // styles it prominent via the centerLine option.
          ctx.beginPath()
          ctx.setLineDash([
            centerLine.dashPx[0] * devicePixelRatio,
            centerLine.dashPx[1] * devicePixelRatio,
          ])
          ctx.lineWidth = centerLine.widthPx * devicePixelRatio
          ctx.strokeStyle = centerLine.color
          ctx.globalAlpha = centerLine.alpha
          strokeSeries(overlay.center)

          // Out-of-bound treatment (all in the one sentinel blue; the glow
          // behind the pen is drawn in drawClear): faint full-height columns
          // locate excursion episodes, and a thin lane along the chart
          // bottom is the excursion timeline.
          const outOfBandAt = (index: number) => {
            const sigma = overlay.sigma[index]
            return (
              sigma !== null &&
              sigma !== undefined &&
              Math.abs(sigma) >= SIGMA_WARN
            )
          }

          // Episode columns: one low-alpha slab per contiguous run.
          ctx.setLineDash([])
          ctx.globalAlpha = 0.07
          ctx.fillStyle = SENTINEL_OUT_OF_BAND_COLOR
          let columnStart = -1
          const flushColumn = (endExclusive: number) => {
            if (columnStart < 0) return
            const x0 = u.valToPos(
              overlay.xs[columnStart] - halfLeft(columnStart),
              'x',
              true
            )
            const x1 = u.valToPos(
              overlay.xs[endExclusive - 1] + halfRight(endExclusive - 1),
              'x',
              true
            )
            ctx.fillRect(x0, u.bbox.top, x1 - x0, u.bbox.height)
            columnStart = -1
          }
          for (let i = 0; i < overlay.xs.length; i++) {
            if (outOfBandAt(i)) {
              // Episodes on either side of a cleaning gap are separate
              // columns; a single slab would paint the gap as an excursion.
              if (columnStart >= 0 && gapBefore(i)) flushColumn(i)
              if (columnStart < 0) columnStart = i
            } else {
              flushColumn(i)
            }
          }
          flushColumn(overlay.xs.length)

          // Excursion lane pinned to the chart bottom: full-opacity blocks
          // with a minimum on-screen width so single bad buckets survive
          // wide zooms, glowing slightly to separate from the background.
          const laneHeight = 2 * devicePixelRatio
          const laneY = u.bbox.top + u.bbox.height - laneHeight
          const minBlockWidth = 3 * devicePixelRatio
          ctx.globalAlpha = 1
          ctx.fillStyle = SENTINEL_OUT_OF_BAND_COLOR
          ctx.shadowColor = SENTINEL_OUT_OF_BAND_COLOR
          ctx.shadowBlur = 2 * devicePixelRatio
          for (let i = 0; i < overlay.xs.length; i++) {
            if (!outOfBandAt(i)) continue
            const x0 = u.valToPos(overlay.xs[i] - halfLeft(i), 'x', true)
            const x1 = u.valToPos(overlay.xs[i] + halfRight(i), 'x', true)
            const width = Math.max(x1 - x0, minBlockWidth)
            const center = (x0 + x1) / 2
            ctx.fillRect(center - width / 2, laneY, width, laneHeight)
          }
          ctx.shadowBlur = 0
          ctx.globalAlpha = 1
        })

        ctx.restore()
      },
    },
  }
}

interface ExperimentRemovedPointsPluginOptions {
  /** Per-tag points the experiment cleaning plan removed. */
  removedPoints: Array<{
    sensorId: string
    /** Epoch-ms x positions, ascending. */
    xs: number[]
    values: (number | null)[]
  }>
  sensorAxisMapping: Record<
    string,
    { axis: string; side: 'left' | 'right'; index: number }
  >
  singleScaleMode: boolean
}

/** Red X markers, matching the old lab's Plotly "removed" trace. */
export const EXPERIMENT_REMOVED_POINT_COLOR = '#ef4444'

/**
 * Experiment Lab cleaning overlay: an X marker at every point the cleaning
 * plan removed (value ops / gate), drawn on the affected pen's own y-scale so
 * the marks sit exactly on the raw series they were removed from.
 */
export function experimentRemovedPointsPlugin(
  options: ExperimentRemovedPointsPluginOptions
): uPlot.Plugin {
  const { removedPoints, sensorAxisMapping, singleScaleMode } = options

  // Same scale resolution rule as sentinelBandsPlugin: in multi-scale mode a
  // pen without a mapped axis has NO scale on this chart, so skip rather
  // than silently drawing against a nonexistent 'y' scale.
  const scaleKeyFor = (sensorId: string): string | null =>
    singleScaleMode ? 'y' : sensorAxisMapping[sensorId] ? sensorId : null

  return {
    hooks: {
      draw: (u: uPlot) => {
        if (removedPoints.length === 0) return

        const ctx = u.ctx
        ctx.save()
        ctx.beginPath()
        ctx.rect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height)
        ctx.clip()

        const arm = 3 * devicePixelRatio
        ctx.lineWidth = 1.5 * devicePixelRatio
        ctx.strokeStyle = EXPERIMENT_REMOVED_POINT_COLOR
        ctx.setLineDash([])

        removedPoints.forEach((series) => {
          const scaleKey = scaleKeyFor(series.sensorId)
          if (scaleKey === null) return

          ctx.beginPath()
          for (let i = 0; i < series.xs.length; i++) {
            const value = series.values[i]
            if (value === null || value === undefined) continue
            const x = u.valToPos(series.xs[i], 'x', true)
            if (x < u.bbox.left || x > u.bbox.left + u.bbox.width) continue
            const y = u.valToPos(value, scaleKey, true)
            ctx.moveTo(x - arm, y - arm)
            ctx.lineTo(x + arm, y + arm)
            ctx.moveTo(x - arm, y + arm)
            ctx.lineTo(x + arm, y - arm)
          }
          ctx.stroke()
        })

        ctx.restore()
      },
    },
  }
}

interface ExperimentMarkersPluginOptions {
  /** Pen the experiment band targets — the dots draw on its y-scale. */
  targetSensorId: string
  /** Epoch ms where the test split begins, or null for no line. */
  testBoundaryMs: number | null
  /** Out-of-band buckets: parallel epoch-ms xs / actual values. */
  outOfBandXs: number[]
  outOfBandValues: number[]
  sensorAxisMapping: Record<
    string,
    { axis: string; side: 'left' | 'right'; index: number }
  >
  singleScaleMode: boolean
}

/** Cap on drawn out-of-band dots; the series decimates above this. */
export const EXPERIMENT_OUT_OF_BAND_MAX_DOTS = 1500

/**
 * Experiment train-result markers, drawn AFTER the pens (draw hook) so they
 * read on top:
 * - a subtle dashed vertical line + "test" label where the test split
 *   begins, so it's obvious which part of the band is held-out scoring;
 * - a high-contrast white dot on each bucket whose actual value left
 *   expected ± band (decimated evenly above the cap so a pathological run
 *   can't paint thousands of dots).
 */
export function experimentMarkersPlugin(
  options: ExperimentMarkersPluginOptions
): uPlot.Plugin {
  const {
    targetSensorId,
    testBoundaryMs,
    outOfBandXs,
    outOfBandValues,
    sensorAxisMapping,
    singleScaleMode,
  } = options

  const scaleKeyFor = (sensorId: string): string | null =>
    singleScaleMode ? 'y' : sensorAxisMapping[sensorId] ? sensorId : null

  return {
    hooks: {
      draw: (u: uPlot) => {
        const ctx = u.ctx
        ctx.save()
        ctx.beginPath()
        ctx.rect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height)
        ctx.clip()

        // Test-split demarcation line.
        const xMin = u.scales.x.min ?? -Infinity
        const xMax = u.scales.x.max ?? Infinity
        if (
          testBoundaryMs !== null &&
          testBoundaryMs >= xMin &&
          testBoundaryMs <= xMax
        ) {
          const x = u.valToPos(testBoundaryMs, 'x', true)
          ctx.beginPath()
          ctx.setLineDash([6 * devicePixelRatio, 6 * devicePixelRatio])
          ctx.lineWidth = 1 * devicePixelRatio
          ctx.strokeStyle = '#94a3b8'
          ctx.globalAlpha = 0.7
          ctx.moveTo(x, u.bbox.top)
          ctx.lineTo(x, u.bbox.top + u.bbox.height)
          ctx.stroke()
          ctx.setLineDash([])
          ctx.globalAlpha = 0.9
          ctx.fillStyle = '#94a3b8'
          ctx.font = `${10 * devicePixelRatio}px sans-serif`
          ctx.textAlign = 'left'
          ctx.textBaseline = 'top'
          ctx.fillText(
            'test →',
            x + 4 * devicePixelRatio,
            u.bbox.top + 4 * devicePixelRatio
          )
          ctx.globalAlpha = 1
        }

        // Out-of-band dots on the target pen's scale.
        const scaleKey = scaleKeyFor(targetSensorId)
        if (scaleKey !== null && outOfBandXs.length > 0) {
          const step = Math.max(
            1,
            Math.ceil(outOfBandXs.length / EXPERIMENT_OUT_OF_BAND_MAX_DOTS)
          )
          const radius = 2.5 * devicePixelRatio
          ctx.lineWidth = 1 * devicePixelRatio
          ctx.fillStyle = '#ffffff'
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.55)'
          for (let i = 0; i < outOfBandXs.length; i += step) {
            const xVal = outOfBandXs[i]
            if (xVal < xMin || xVal > xMax) continue
            const x = u.valToPos(xVal, 'x', true)
            const y = u.valToPos(outOfBandValues[i], scaleKey, true)
            ctx.beginPath()
            ctx.arc(x, y, radius, 0, Math.PI * 2)
            ctx.fill()
            ctx.stroke()
          }
        }

        ctx.restore()
      },
    },
  }
}

interface TooltipPluginOptions {
  /** Mutable ref whose `.current` holds the latest cursor-move handler. Using
   *  a ref allows the plugin identity to remain stable across re-renders so
   *  that the plugins array (and therefore uPlotOptions) doesn't change when
   *  only the callback changes. */
  onCursorMoveRef: {
    current: ((idx: number | null, u: uPlot) => void) | undefined
  }
  onCursorLeaveRef: { current: (() => void) | undefined }
}

export function tooltipPlugin(options: TooltipPluginOptions): uPlot.Plugin {
  const { onCursorMoveRef, onCursorLeaveRef } = options

  let prevIdx: number | null | undefined = null
  let isDragging = false

  return {
    hooks: {
      ready: (u: uPlot) => {
        const over = u.over

        over.addEventListener('mousedown', () => {
          isDragging = false
        })
        over.addEventListener('mousemove', (e: MouseEvent) => {
          if (e.buttons === 1) {
            isDragging = true
          }
        })
        over.addEventListener('mouseup', () => {
          isDragging = false
        })
      },
      setCursor: (u: uPlot) => {
        // Suppress tooltip updates during drag-to-zoom to avoid React state
        // updates that could trigger re-renders and chart recreation.
        if (isDragging) return

        const idx = u.cursor.idx

        if (idx !== prevIdx) {
          prevIdx = idx
          if (idx !== null && idx !== undefined) {
            onCursorMoveRef.current?.(idx, u)
          } else {
            onCursorLeaveRef.current?.()
          }
        }
      },
    },
  }
}

export function exportPlugin(): uPlot.Plugin {
  return {
    hooks: {
      ready: (u: uPlot) => {
        const button = document.createElement('button')
        button.textContent = 'PNG'
        button.style.cssText = `
          position: absolute;
          top: 4px;
          right: 4px;
          z-index: 100;
          padding: 2px 8px;
          background: ${CHART_COLORS.LEGEND_BACKGROUND};
          color: ${CHART_COLORS.TEXT};
          border: 1px solid ${CHART_COLORS.LEGEND_BORDER};
          border-radius: 3px;
          cursor: pointer;
          font: ${UPLOT_TICK_FONT};
          opacity: 0;
          transition: opacity 0.2s;
        `

        u.root.style.position = 'relative'
        u.root.appendChild(button)

        u.root.addEventListener('mouseenter', () => {
          button.style.opacity = '1'
        })
        u.root.addEventListener('mouseleave', () => {
          button.style.opacity = '0'
        })

        button.addEventListener('click', () => {
          const canvas = u.ctx.canvas

          const link = document.createElement('a')
          link.download = 'time-series-chart.png'
          link.href = canvas.toDataURL('image/png')
          link.click()
        })
      },
    },
  }
}
